import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperIcon,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { isHttpErrorResponse } from '@ipg/ngx-frontend-toolset';
import { catchError, finalize, tap, throwError } from 'rxjs';

import { UnitResponseModel } from '../../../units/units.model';
import { UnitService } from '../../../units/services/unit.service';
import { InteractionService } from '../../../../services/interaction.service';
import {
  ContactResolution,
  GotoChainEntry,
  MovePreviewContact,
  TemplateMovePreviewResponse,
} from '../../models/move-template.models';

interface ContactRow extends MovePreviewContact {
  action: 'replace' | 'import';
  targetContactId?: string;
}

@Component({
  selector: 'app-move-template-wizard',
  standalone: true,
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperIcon,
    MatStepperNext,
    MatStepperPrevious,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    FormsModule,
  ],
  templateUrl: './move-template-wizard.component.html',
  styleUrl: './move-template-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveTemplateWizardComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _interactions = inject(InteractionService);
  private readonly _units = inject(UnitService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _el = inject(ElementRef);

  // State
  protected readonly correlationId = signal<string | null>(null);
  protected readonly templateLabel = signal<string>('');
  protected readonly templateUnitId = signal<string | null>(null);
  protected readonly units = signal<UnitResponseModel[]>([]);
  protected readonly selectedUnitId = signal<string | null>(null);
  protected readonly preview = signal<TemplateMovePreviewResponse | null>(null);
  protected readonly contactRows = signal<ContactRow[]>([]);
  protected readonly isLoadingPreview = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly maxStepReached = signal(0);

  // Derived
  protected readonly availableUnits = computed(() => {
    const unitId = this.templateUnitId();
    if (!unitId) return this.units();
    return this.units().filter((u) => u._id !== unitId && !u.deleteAt);
  });

  protected readonly sourceUnit = computed(() => {
    const unitId = this.templateUnitId();
    if (!unitId) return null;
    return this.units().find((u) => u._id === unitId) ?? null;
  });

  protected readonly selectedUnit = computed(() => {
    const id = this.selectedUnitId();
    return this.units().find((u) => u._id === id) ?? null;
  });

  protected readonly hasContacts = computed(() => {
    const p = this.preview();
    return p != null && p.contactsInUse.length > 0;
  });

  protected readonly isContactStepValid = computed(() => {
    if (!this.preview()) return false;
    const rows = this.contactRows();
    return rows.every(
      (r) => r.action === 'import' || (r.action === 'replace' && r.targetContactId)
    );
  });

  /** Auto-resolved goto chain from the preview. */
  protected readonly gotoChain = computed(() => {
    const p = this.preview();
    return p?.gotoChain ?? [];
  });

  /** Number of media that will be automatically copied. */
  protected readonly mediaCopyCount = computed(() => {
    const p = this.preview();
    return p?.brokenMedia.length ?? 0;
  });

  /** Templates that will be reused (info banners). */
  protected readonly reusedTemplates = computed(() =>
    this.gotoChain().filter((e) => e.autoResolvedAction === 'reuse')
  );



  ngOnInit(): void {
    const correlationId = this._route.snapshot.paramMap.get('correlationId');
    if (!correlationId) {
      this._router.navigate(['/templates']);
      return;
    }

    this.correlationId.set(correlationId);

    // Look up the template by correlationId via the dedicated
    // move-info endpoint, avoiding the page-size limit issue.
    this._interactions
      .getMoveInfo(correlationId)
      .subscribe({
        next: (info) => {
          this.templateLabel.set(info.label);
          this.templateUnitId.set(info.unitId);

          if (info.moveId) {
            this._snackbar.open(
              'Deze template wordt momenteel verplaatst.',
              'OK'
            );
            this._router.navigate(['/templates']);
            return;
          }

          if (info.shareId) {
            this._snackbar.open(
              'Gedeelde templates kunnen niet worden verplaatst. Verwijder eerst het delen.',
              'OK'
            );
            this._router.navigate(['/templates']);
          }
        },
        error: () => {
          this._snackbar.open('Template niet gevonden', 'OK');
          this._router.navigate(['/templates']);
        },
      });

    this._units.getByUser().subscribe({
      next: (units) => this.units.set(units),
    });
  }

  onUnitSelected(): void {
    const correlationId = this.correlationId();
    const unitId = this.selectedUnitId();
    if (!correlationId || !unitId) return;

    this.isLoadingPreview.set(true);
    this.preview.set(null);
    this.contactRows.set([]);

    this._interactions
      .getMovePreview(correlationId, unitId)
      .pipe(finalize(() => this.isLoadingPreview.set(false)))
      .subscribe({
        next: (preview) => {
          this.preview.set(preview);

          // Initialize contact rows (auto-match or import)
          this.contactRows.set(
            preview.contactsInUse.map((c) => {
              const match = preview.targetUnitContacts.find(
                (t) =>
                  t.name === c.name &&
                  t.phoneNumber === c.phoneNumber &&
                  t.email === c.email
              );
              return match
                ? { ...c, action: 'replace' as const, targetContactId: match._id }
                : { ...c, action: 'import' as const };
            })
          );
        },
        error: (error) => {
          if (
            isHttpErrorResponse(error) &&
            error.error?.detail?.errorCode === 'TEMPLATE_IS_SHARED'
          ) {
            this._snackbar.open(
              'Gedeelde templates kunnen niet worden verplaatst.',
              'OK'
            );
            this._router.navigate(['/templates']);
          } else {
            this._snackbar.open(
              'Er is iets misgegaan bij het laden van het voorbeeld',
              'OK'
            );
          }
        },
      });
  }

  onContactActionChange(index: number, action: 'replace' | 'import'): void {
    this.contactRows.update((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], action, targetContactId: undefined };
      return updated;
    });
  }

  onTargetContactChange(index: number, targetContactId: string): void {
    this.contactRows.update((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], targetContactId };
      return updated;
    });
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.maxStepReached.update((current) => Math.max(current, event.selectedIndex));

    // Scroll the active step header into the visible area of the scrollable header
    const headers = this._el.nativeElement.querySelectorAll('.mat-step-header');
    headers[event.selectedIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  submit(): void {
    const correlationId = this.correlationId();
    const targetUnitId = this.selectedUnitId();
    if (!correlationId || !targetUnitId) return;

    // Contact resolutions are the only user-provided input.
    // goto_resolutions and media_resolutions are omitted
    // so the backend auto-generates them from the preview.
    const contactResolutions: ContactResolution[] = this.contactRows().map((r) => ({
      sourceContactId: r._id,
      action: r.action,
      ...(r.action === 'replace' && r.targetContactId
        ? { targetContactId: r.targetContactId }
        : {}),
    }));

    this.isSubmitting.set(true);

    this._interactions
      .moveTemplate(correlationId, {
        targetUnitId,
        contactResolutions,
      })
      .pipe(
        tap(() => {
          this._snackbar.open(
            `Template "${this.templateLabel()}" wordt verplaatst...`,
            'OK'
          );
          this._router.navigate(['/templates']);
        }),
        catchError((error) => {
          this._snackbar.open(
            'Er is iets misgegaan bij het verplaatsen. Probeer het later nog eens.',
            'OK'
          );
          return throwError(() => error);
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  getTargetContactName(targetContactId?: string): string {
    if (!targetContactId) return '—';
    const contact = this.preview()?.targetUnitContacts.find(
      (c) => c._id === targetContactId
    );
    return contact?.name ?? '—';
  }

  /** Helper to get the action icon for a goto chain entry. */
  getActionIcon(entry: GotoChainEntry): string {
    switch (entry.autoResolvedAction) {
      case 'reuse':
        return 'check_circle';
      case 'copy':
        return 'content_copy';
      case 'move':
        return 'drive_file_move';
    }
  }

  /** Helper to get the action label for a goto chain entry. */
  getActionLabel(entry: GotoChainEntry): string {
    switch (entry.autoResolvedAction) {
      case 'reuse':
        return 'Hergebruiken';
      case 'copy':
        return 'Kopiëren';
      case 'move':
        return 'Verplaatsen';
    }
  }

  /** Helper to get the action CSS class for a goto chain entry. */
  getActionClass(entry: GotoChainEntry): string {
    switch (entry.autoResolvedAction) {
      case 'reuse':
        return 'text-purple-600';
      case 'copy':
        return 'text-blue-600';
      case 'move':
        return 'text-green-600';
    }
  }

  cancel(): void {
    this._router.navigate(['/templates']);
  }
}
